
import React, { useState, useEffect, useRef } from 'react';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { ExperimentalClassInfoSection } from './components/ExperimentalClassInfoSection';
import { AboutSection } from './components/AboutSection';
import { CallToActionFooter } from './components/CallToActionFooter';
import { ManifestoSection } from './components/ManifestoSection';
// import { GuidedMelodySection } from './components/GuidedMelodySection';
import { FourPillarsSection } from './components/FourPillarsSection';
import { MasterySection } from './components/MasterySection';
import { VideoTestimonialsSection } from './components/VideoTestimonialsSection';
import { TextTestimonialsSection } from './components/TextTestimonialsSection';
import { FaqSection } from './components/FaqSection';
import { ProfessorTip } from './components/ProfessorTip';
import { FloatingWhatsAppButton } from './components/FloatingWhatsAppButton';
import { useIsMobile } from './hooks/useIsMobile';
import Preloader from './components/Preloader';
import { DynamicBackground } from './components/DynamicBackground';
import { PlansSection } from './components/PlansSection';
import { MethodologyDifferentiatorsSection } from './components/MethodologyDifferentiatorsSection';
import { PortalAnimationSection } from './components/PortalAnimationSection';
import { useTranslation } from './i18n/LanguageContext';

export default function App(): React.ReactElement {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [currentBg, setCurrentBg] = useState('bg-brand-secondary');

  const sectionRefs = useRef<Map<string, Element>>(new Map());

  const addToRefs = (el: Element | null, key: string) => {
    const map = sectionRefs.current;
    if (el) {
      map.set(key, el);
    } else {
      map.delete(key);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 400); 
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const bgClass = (entry.target as HTMLElement).dataset.bg;
            if (bgClass && bgClass !== currentBg) {
              setCurrentBg(bgClass);
            }
          }
        });
      },
      { rootMargin: "-40% 0px -40% 0px", threshold: 0 }
    );

    const refs = Array.from(sectionRefs.current.values());
    refs.forEach(ref => { if (ref instanceof Element) observer.observe(ref); });

    return () => {
      refs.forEach(ref => { if (ref instanceof Element) observer.unobserve(ref); });
    };
  }, [isLoading, currentBg]);
  
  return (
    <div className="font-sans antialiased text-brand-text selection:bg-brand-accent selection:text-brand-primary bg-brand-primary min-h-screen">
      {isLoading && <Preloader />}
      <Navbar />
      <main className="overflow-x-clip">
        <Hero />
        <div className={`transition-colors duration-[1500ms] cubic-bezier(0.16, 1, 0.3, 1) ${currentBg}`}>
          <div className="relative">
            <DynamicBackground />
            <div className="relative z-10 pt-16 md:pt-24">
              <div ref={(node) => addToRefs(node, 'expClass')} data-bg="bg-brand-secondary">
                <ExperimentalClassInfoSection />
              </div>
              <div ref={(node) => addToRefs(node, 'manifesto')} data-bg="bg-brand-primary">
                <ManifestoSection />
              </div>
              {/* Seção desativada por problemas de áudio no celular:
              <div ref={(node) => addToRefs(node, 'guidedMelody')} data-bg="bg-brand-secondary">
                <GuidedMelodySection />
              </div> 
              */}
              <div ref={(node) => addToRefs(node, 'pilares')} data-bg="bg-brand-primary">
                <FourPillarsSection />
              </div>
              <div ref={(node) => addToRefs(node, 'mastery')} data-bg="bg-brand-secondary">
                <MasterySection />
              </div>
              <ProfessorTip tipText={t('tips.tip1')} />
              <div ref={(node) => addToRefs(node, 'metodologia')} data-bg="bg-brand-primary">
                <MethodologyDifferentiatorsSection />
              </div>
              <div ref={(node) => addToRefs(node, 'about')} data-bg="bg-brand-secondary">
                <AboutSection />
              </div>
              <div ref={(node) => addToRefs(node, 'videoTestimonials')} data-bg="bg-brand-primary">
                <VideoTestimonialsSection />
              </div>
              <div ref={(node) => addToRefs(node, 'textTestimonials')} data-bg="bg-brand-secondary">
                <TextTestimonialsSection />
              </div>
              <div ref={(node) => addToRefs(node, 'plans')} data-bg="bg-brand-primary">
                <PlansSection />
              </div>
              <div ref={(node) => addToRefs(node, 'portal')} data-bg="bg-brand-secondary">
                <PortalAnimationSection />
              </div>
              <div ref={(node) => addToRefs(node, 'faq')} data-bg="bg-brand-primary">
                <FaqSection />
              </div>
              <ProfessorTip title={t('tips.tip2Title')} tipText={t('tips.tip2')} />
            </div>
          </div>
        </div>
      </main>
      <CallToActionFooter />
      <FloatingWhatsAppButton />
    </div>
  );
}
